import { fetchCategoryHierarchyByKey } from "@/apis";
import type { Category } from "@/types";
import type { ICategoryStrategy, NextAction } from "./ICategoryStrategy";

type SellCategory = Category & {
    hasChildren: boolean;
    children?: Category[] | null;
};

export class SellCategoryStrategy implements ICategoryStrategy {
    readonly useUrlDepth = true;
    readonly autoSelectSingleCategory = true;

    constructor(
        private readonly categoryKey: string,
        private readonly postType: string,
        private readonly userPlanId: string = "",
        private readonly mode: string = ""
    ) {}

    private createSearchParams(includePostType = false) {
        const search = new URLSearchParams();
        if (includePostType) search.set("postType", this.postType);
        if (this.userPlanId) search.set("userPlanId", this.userPlanId);
        if (this.mode) search.set("mode", this.mode);
        return search;
    }

    async fetchCategories(slug: string): Promise<Category[]> {
        const response = await fetchCategoryHierarchyByKey(slug, {
            sendChildren: true,
            sendAncestors: false,
        });
        const category = response?.data?.response as SellCategory | undefined;

        if (!category) return [];
        return category.hasChildren ? category.children ?? [] : [category];
    }

    onLeafCategorySelected(category?: Category): NextAction {
        const leafCategoryKey = category?.key || this.categoryKey;
        if (!leafCategoryKey) return { kind: "drilldown" };
        const search = this.createSearchParams();
        const query = search.toString();

        return {
            kind: "navigate",
            to: `/posts/${this.postType}/${leafCategoryKey}${query ? `?${query}` : ""}`,
            replace: true
        };
    }

    getNextAction(category: Category): NextAction {
        if (!(category as SellCategory).hasChildren) {
            return this.onLeafCategorySelected(category);
        }

        const search = this.createSearchParams(true);
        return {
            kind: "navigate",
            to: `/sell/${category.key}?${search.toString()}`
        };
    }
}
